/**
 * New Analysis Page - Placeholder workflow for uploading data
 */
import React, { useEffect, useRef, useState } from 'react';
import { UploadCloud, FileText, ShieldCheck, ArrowLeft, X, CheckCircle2, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import dataService from '../services/data';
import { useAuthStore } from '../store/authStore';

const NewAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [fundingStage, setFundingStage] = useState('');
  const [summary, setSummary] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupMode, setGroupMode] = useState<'existing' | 'new'>('existing');
  const [newGroupName, setNewGroupName] = useState('');
  const [availableGroups, setAvailableGroups] = useState<Array<{ name: string; count: number }>>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setGroupsLoading(true);
      const res = await dataService.fetchCompanyGroups();
      if (!cancelled && res.data) {
        setAvailableGroups(res.data);
        if (res.data.length === 0) {
          setGroupMode('new');
        }
      }
      if (!cancelled) setGroupsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const addFiles = (fileList: FileList | null) => {
    if (!fileList || !fileList.length) return;

    const newFiles: File[] = Array.from(fileList);
    setSelectedFiles((prev) => {
      const existingNames = new Set(prev.map((file) => file.name));
      const deduped = newFiles.filter((file) => !existingNames.has(file.name));
      return [...prev, ...deduped];
    });
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files);
    // reset value to allow re-upload of same file name
    event.target.value = '';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    addFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const removeFile = (fileName: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const resetForm = () => {
    setCompanyName('');
    setIndustry('');
    setFundingStage('');
    setSummary('');
    setNotes('');
    setSelectedFiles([]);
    setGroupName('');
    setNewGroupName('');
    setGroupMode(availableGroups.length > 0 ? 'existing' : 'new');
  };

  const resolvedGroupName = (): string => {
    if (groupMode === 'new') return newGroupName.trim();
    return groupName.trim();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!companyName.trim()) {
      toast.error('Please provide the company name.');
      return;
    }

    if (selectedFiles.length === 0 && !summary.trim() && !notes.trim()) {
      toast.error('Upload at least one document or include diligence notes.');
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append('company_name', companyName.trim());
      if (industry.trim()) formData.append('industry', industry.trim());
      if (fundingStage.trim()) formData.append('funding_stage', fundingStage.trim());
      if (summary.trim()) formData.append('summary', summary.trim());
      if (notes.trim()) formData.append('notes', notes.trim());
      if (user?.email) formData.append('owner_email', user.email);
      const grp = resolvedGroupName();
      if (grp) formData.append('group_name', grp);

      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      const result = await dataService.createAnalysis(formData);

      if (result.error) {
        toast.error(result.error);
        return;
      }

      const data: any = result.data;
      if (data?.matched_existing) {
        toast.success(
          `Matched existing company: ${data.company_name} (${data.match_kind} match) — files attached and research re-queued.`
        );
      } else {
        toast.success('Analysis submitted for review.');
      }
      resetForm();
      navigate('/analyses');
    } catch (error: any) {
      console.error('Create analysis error:', error);
      toast.error('Something went wrong while submitting the analysis.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <form className="max-w-4xl mx-auto space-y-8" onSubmit={handleSubmit}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Start a New Analysis</h1>
            <p className="text-gray-600 mt-2">
              Upload company materials and configure the scope of the VC due diligence run.
            </p>
          </div>
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to dashboard
          </Link>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Company name
              </label>
              <input
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                placeholder="Example: Relay Robotics"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Industry
              </label>
              <input
                value={industry}
                onChange={(event) => setIndustry(event.target.value)}
                placeholder="Fintech, Biotech, Climate..."
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Funding stage
              </label>
              <select
                value={fundingStage}
                onChange={(event) => setFundingStage(event.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select stage</option>
                <option value="Pre-Seed">Pre-Seed</option>
                <option value="Seed">Seed</option>
                <option value="Series A">Series A</option>
                <option value="Series B">Series B</option>
                <option value="Growth">Growth</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Executive summary / pasted context
              </label>
              <textarea
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
                rows={4}
                placeholder="Paste the deal memo, executive summary, or key context here."
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="pt-6 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">
                Group
              </label>
              {availableGroups.length > 0 && (
                <button
                  type="button"
                  onClick={() =>
                    setGroupMode((m) => (m === 'existing' ? 'new' : 'existing'))
                  }
                  className="inline-flex items-center text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  {groupMode === 'existing' ? (
                    <>
                      <Plus className="w-3 h-3 mr-1" />
                      Create new group instead
                    </>
                  ) : (
                    <>
                      <ArrowLeft className="w-3 h-3 mr-1" />
                      Pick an existing group
                    </>
                  )}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Optional — groups let you scope the Companies page to a single
              partner's intake (e.g. MTW, BMP, AFG).
            </p>
            {groupMode === 'existing' ? (
              <select
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                disabled={groupsLoading}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
              >
                <option value="">
                  {groupsLoading ? 'Loading groups…' : 'No group'}
                </option>
                {availableGroups.map((g) => (
                  <option key={g.name} value={g.name}>
                    {g.name} ({g.count})
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={newGroupName}
                onChange={(event) => setNewGroupName(event.target.value)}
                placeholder="New group name (e.g. MTW Cohort 4)"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-6 text-center">
            <UploadCloud className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Documents</h3>
            <p className="text-sm text-gray-600">
              Pitch decks, financial models, customer lists, and due diligence files.
            </p>
          </div>
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-6 text-center">
            <FileText className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Provide Context</h3>
            <p className="text-sm text-gray-600">
              Company stage, key questions, competitive angles, and focus areas.
            </p>
          </div>
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-6 text-center">
            <ShieldCheck className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Compliance Ready</h3>
            <p className="text-sm text-gray-600">
              Data stored securely and only accessible inside your workspace.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-8 space-y-6">
          <div
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center transition-colors hover:border-blue-400"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <UploadCloud className="w-12 h-12 mx-auto text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Drop files here</h3>
            <p className="text-sm text-gray-600 mb-4">
              Drag and drop documents or select files from your device. Supported formats include PDF, PPTX, DOCX, XLSX, and CSV.
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              Browse files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileInput}
            />
          </div>

          {selectedFiles.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Files ready for analysis
              </h3>
              <ul className="space-y-3">
                {selectedFiles.map((file) => (
                  <li
                    key={file.name}
                    className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                  >
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(file.name)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Key diligence questions or context
            </label>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={4}
              placeholder="Example: Focus on customer retention metrics and pipeline performance in Europe."
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              This demo stores uploads in memory only. In production, documents would be encrypted and processed securely.
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit for analysis'}
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 text-sm text-blue-800">
          Prefer a white-glove kickoff? Email{' '}
          <a href="mailto:hello@getventureos.ai" className="font-semibold">
            hello@getventureos.ai
          </a>{' '}
          and the team will set up the diligence run for you.
        </div>
      </form>
    </DashboardLayout>
  );
};

export default NewAnalysisPage;
